// Zero-copy transfer support. Transferring an ArrayBuffer hands ownership to the other
// thread instead of structured-cloning it — a large win for binary payloads (images,
// audio, buffers). SharedArrayBuffer is intentionally NOT transferable (it's shared),
// so it is excluded from auto-collection.

const TRANSFER = Symbol.for('nodets.transfer')

export interface Transferred<T> {
  [TRANSFER]: true
  value: T
  transferables: Transferable[]
}

/**
 * Mark an argument so its ArrayBuffers are transferred (zero-copy) to the worker
 * instead of copied. Pass explicit `transferables`, or let them be auto-detected.
 *
 *   pool.run(transfer({ pixels }, [pixels.buffer]))
 *   pool.run(transfer({ pixels }))   // auto-detects pixels.buffer
 */
export function transfer<T>(value: T, transferables?: Transferable[]): Transferred<T> {
  return {
    [TRANSFER]: true,
    value,
    transferables: transferables ?? transferablesOf(value),
  }
}

export function isTransferred(v: unknown): v is Transferred<unknown> {
  return typeof v === 'object' && v !== null && (v as any)[TRANSFER] === true
}

/** Recursively collect transferable ArrayBuffers from a value (excludes SharedArrayBuffer). */
export function transferablesOf(value: unknown): Transferable[] {
  const acc = new Set<Transferable>()
  collect(value, acc, 0)
  return [...acc]
}

function collect(value: unknown, acc: Set<Transferable>, depth: number): void {
  if (depth > 6 || value == null || typeof value !== 'object') return
  if (value instanceof ArrayBuffer) {
    acc.add(value)
    return
  }
  if (ArrayBuffer.isView(value)) {
    // Only real ArrayBuffers are transferable; SharedArrayBuffer must stay shared.
    const buf = (value as ArrayBufferView).buffer
    if (buf instanceof ArrayBuffer) acc.add(buf)
    return
  }
  if (Array.isArray(value)) {
    for (const v of value) collect(v, acc, depth + 1)
    return
  }
  for (const k in value as Record<string, unknown>) {
    collect((value as Record<string, unknown>)[k], acc, depth + 1)
  }
}
